import { useState, useCallback } from "react";
import { phoneValidator, sanitizePhone } from "./phoneValidator";
import { emailValidator, sanitizeEmail } from "./emailValidator";

export type ContactValidationMessages = {
  invalidEmail: string;
  invalidPhone: string;
  invalidPhoneOrEmail: string;
};

/**
 * Sanitize + validate a contact for the configured type. Pure, so components that own their own
 * contact state (e.g. one seeded from saved info) share this instead of reimplementing the branches.
 */
export function validateContact(params: {
  contactType: 'email' | 'phone_number' | undefined;
  value: string;
  messages: ContactValidationMessages;
}): { value: string, error: string } {
  const { contactType, messages } = params;
  if (contactType === 'email') {
    const value = sanitizeEmail(params.value);
    return { value, error: emailValidator(value) ? '' : messages.invalidEmail };
  }
  if (contactType === 'phone_number') {
    const value = sanitizePhone(params.value);
    return { value, error: phoneValidator(value) ? '' : messages.invalidPhone };
  }
  // Either is accepted: sanitize as an email (lowercase + strip whitespace, which leaves digits
  // alone) rather than by guessing the shape — sanitizePhone would erase a half-typed address one
  // keystroke at a time. Then accept whichever form validates.
  const value = sanitizeEmail(params.value);
  return {
    value,
    error: (phoneValidator(value) || emailValidator(value)) ? '' : messages.invalidPhoneOrEmail,
  };
}

export function useContactValidation(params: {
  contactType: 'email' | 'phone_number' | undefined;
  messages: ContactValidationMessages;
}) {
  const { contactType, messages } = params;
  const [contact, setContact] = useState('');
  const [contactValidationError, setContactValidationError] = useState('');

  const contactOnChange = useCallback((value: string) => {
    const result = validateContact({ contactType, value, messages });
    setContact(result.value);
    setContactValidationError(result.error);
  }, [contactType, messages]);

  return { contact, contactValidationError, contactOnChange };
}
